import { holderIndex } from './holder-index';
import { poolSummaries, type PoolSummary } from './icpswap';

export const ICPSWAP_FACTORY = '4mmnk-kiaaa-aaaag-qbllq-cai';
export interface DexToken {
  ledger: string; name: string | null; symbol: string | null; logo: string | null;
  decimals: number | null; poolCount: number; primaryPool: string | null;
  priceUsd: number | null; liquidityUsd: number | null; volume24hUsd: number | null;
}
export interface DexPool {
  id: string; token0: string; token1: string;
}
export interface DexRegistry {
  tokens: DexToken[]; pools: number; tokenCount: number;
  asOf: number | null; lastAttempt: number | null; registryComplete: boolean;
  metadataComplete: boolean; metadataCount: number;
  metricsAsOf: number | null; metricsComplete: boolean; metricsPoolCount: number;
  error?: string;
}
export interface DexCatalog {
  tokens: DexToken[]; poolRows: DexPool[]; asOf: number | null; lastAttempt: number | null;
  complete: boolean; metadataPending: number; error?: string;
}

function text(value: string | null | undefined, limit: number): string | null {
  return typeof value === 'string' && value.trim() ? value.trim().slice(0, limit) : null;
}
export function dexLogo(value: string | null | undefined): string | null {
  if (!value || value.length > 100_000) return null;
  if (/^data:image\/(png|jpeg|gif|webp);base64,[a-z\d+/=]+$/i.test(value)) return value;
  try { const url = new URL(value); return url.protocol === 'https:' ? url.href : null; } catch { return null; }
}
export function dexMetric(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? value : null;
  if (typeof value !== 'string' || !/^(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(value.trim())) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

/** Join analytics only to factory-registered pool IDs AND their exact ledger pair.
 * Missing pools leave aggregate token figures unknown, rather than making an
 * incomplete sum look like full volume or liquidity. Price is a quote from the
 * deepest matched pool with a positive USD price, not a blend of unlike pairs. */
export function applyDexMetrics(catalog: DexCatalog, summaries: Map<string, PoolSummary> | null, metricsAsOf: number | null, metricsError?: string): DexRegistry {
  const membership = new Map<string, DexPool[]>();
  const matched = new Map<string, PoolSummary>();
  for (const pool of catalog.poolRows) {
    for (const ledger of new Set([pool.token0, pool.token1])) {
      const pools = membership.get(ledger) ?? []; pools.push(pool); membership.set(ledger, pools);
    }
    const row = summaries?.get(pool.id);
    if (row && row.poolId === pool.id && (row.token0LedgerId === pool.token0 && row.token1LedgerId === pool.token1 || row.token0LedgerId === pool.token1 && row.token1LedgerId === pool.token0)) matched.set(pool.id, row);
  }
  let allMetrics = catalog.poolRows.length > 0 && matched.size === catalog.poolRows.length;
  const tokens = catalog.tokens.map(token => {
    const pools = membership.get(token.ledger) ?? [];
    let liquidity: number | null = pools.length ? 0 : null;
    let volume: number | null = pools.length ? 0 : null;
    let quote: { price: number; liquidity: number; pool: string; row: PoolSummary } | null = null;
    let metadata: { name: string | null; symbol: string | null } = { name: null, symbol: null };
    for (const pool of pools) {
      const row = matched.get(pool.id);
      const tvl = dexMetric(row?.tvlUSD), daily = dexMetric(row?.volumeUSD24H);
      liquidity = liquidity === null || tvl === null ? null : liquidity + tvl;
      volume = volume === null || daily === null ? null : volume + daily;
      if (!row) continue;
      const zero = row.token0LedgerId === token.ledger;
      const price = dexMetric(zero ? row.token0Price : row.token1Price);
      // These names are optional additions supplied by ICPSwap's analytics.
      const names = row as PoolSummary & { token0Name?: string; token1Name?: string; token0Symbol?: string; token1Symbol?: string };
      const values = { name: text(zero ? names.token0Name : names.token1Name, 200), symbol: text(zero ? names.token0Symbol : names.token1Symbol, 40) };
      metadata = { name: metadata?.name ?? values.name, symbol: metadata?.symbol ?? values.symbol };
      if (price !== null && price > 0 && tvl !== null && tvl > 0 && (!quote || tvl > quote.liquidity || tvl === quote.liquidity && pool.id < quote.pool)) {
        quote = { price, liquidity: tvl, pool: pool.id, row };
        metadata = { name: values.name ?? metadata?.name ?? null, symbol: values.symbol ?? metadata?.symbol ?? null };
      }
    }
    liquidity = dexMetric(liquidity); volume = dexMetric(volume);
    if (liquidity === null || volume === null || !quote) allMetrics = false;
    return { ...token, name: token.name ?? metadata?.name ?? null, symbol: token.symbol ?? metadata?.symbol ?? null,
      poolCount: pools.length, primaryPool: quote?.pool ?? token.primaryPool,
      priceUsd: quote?.price ?? null, liquidityUsd: liquidity, volume24hUsd: volume };
  });
  return { tokens, pools: catalog.poolRows.length, tokenCount: tokens.length,
    asOf: catalog.asOf, lastAttempt: catalog.lastAttempt, registryComplete: catalog.complete,
    metadataComplete: tokens.every(token => token.name && token.symbol), metadataCount: tokens.filter(token => token.name && token.symbol).length,
    metricsAsOf, metricsComplete: allMetrics, metricsPoolCount: matched.size,
    error: [catalog.error, metricsError].filter(Boolean).join(' ') || undefined };
}

export interface DexPage<T> {
  items: T[]; total: bigint; next_offset: [] | [bigint]; version: bigint; page_valid: boolean;
}
class ChangedCatalog extends Error {}
/** Read a frozen membership version. This rejects silent truncation, duplicate
 * pages and regressing cursors; the caller retries a concurrent refresh once. */
export async function collectDexPages<T>(read: (offset: bigint) => Promise<DexPage<T>>, version: bigint, key: (row: T) => string, max = 40_000): Promise<T[]> {
  let offset = 0n, total: bigint | null = null;
  const result: T[] = [], seen = new Set<string>();
  do {
    const page = await read(offset);
    if (!page.page_valid || page.version !== version) throw new ChangedCatalog('The ICPSwap catalog refreshed while loading.');
    if (page.total < 0n || page.total > BigInt(max) || total !== null && page.total !== total) throw Error('ICPSwap index returned an inconsistent catalog size.');
    total = page.total;
    if (page.items.length > 200 || offset + BigInt(page.items.length) > total) throw Error('ICPSwap index returned an invalid page.');
    for (const row of page.items) { const id = key(row); if (seen.has(id)) throw Error('ICPSwap index returned a duplicate catalog entry.'); seen.add(id); result.push(row); }
    const next = offset + BigInt(page.items.length);
    if (next < total) {
      if (!page.items.length || page.next_offset[0] !== next) throw Error('ICPSwap index returned an incomplete catalog.');
      offset = next;
    } else {
      if (page.next_offset.length) throw Error('ICPSwap index returned an invalid final page.');
      return result;
    }
  } while (true);
}

const millis = (value: [] | [bigint]): number | null => value.length ? Number(value[0] / 1_000_000n) : null;
export async function readDexCatalog(): Promise<DexCatalog> {
  const service = await holderIndex();
  for (let attempt = 0; attempt < 2; attempt++) {
    const status = await service.dex_status();
    if (status.factory.toText() !== ICPSWAP_FACTORY && import.meta.env.VITE_DFX_NETWORK !== 'local') throw Error('The ICPSwap index uses an unexpected factory.');
    if (!('native' in status.mode) && import.meta.env.VITE_DFX_NETWORK !== 'local') throw Error('Development ICPSwap catalogs cannot be shown on mainnet.');
    try {
      const [tokens, pools] = await Promise.all([
        collectDexPages(offset => service.dex_tokens({ offset, limit: 200n, expected_version: [status.version], search: '' }), status.version, token => token.ledger.toText()),
        collectDexPages(offset => service.dex_pools({ offset, limit: 200n, expected_version: [status.version], ledger: [] }), status.version, pool => pool.canisterId.toText(), 20_000),
      ]);
      if (BigInt(tokens.length) !== status.token_count || BigInt(pools.length) !== status.pool_count) throw new ChangedCatalog('The ICPSwap catalog changed while loading.');
      return { tokens: tokens.map(token => ({ ledger: token.ledger.toText(), name: text(token.name[0], 200), symbol: text(token.symbol[0], 40), logo: dexLogo(token.logo[0]), decimals: token.decimals.length && token.decimals[0] <= 255n ? Number(token.decimals[0]) : null,
        poolCount: Number(token.pool_count), primaryPool: token.primary_pool.toText(), priceUsd: null, liquidityUsd: null, volume24hUsd: null })),
        poolRows: pools.map(pool => ({ id: pool.canisterId.toText(), token0: pool.token0.address, token1: pool.token1.address })),
        asOf: millis(status.last_success_at), lastAttempt: millis(status.last_attempt_at), complete: status.complete && !status.truncated,
        metadataPending: Number(status.metadata_pending), error: status.error[0] ?? (status.last_success_at.length ? undefined : 'The canister is building the ICPSwap catalog. It will appear automatically after its first successful refresh.') };
    } catch (error) { if (error instanceof ChangedCatalog && attempt === 0) continue; throw error; }
  }
  throw Error('The ICPSwap catalog is refreshing. Try again shortly.');
}

let metricCache: { rows: Map<string, PoolSummary>; at: number } | undefined;
const metricTimes = new WeakMap<Map<string, PoolSummary>, number>();
async function metrics(): Promise<{ rows: Map<string, PoolSummary> | null; at: number | null; error?: string }> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const rows = await Promise.race([poolSummaries(), new Promise<never>((_, reject) => { timer = setTimeout(() => reject(Error('ICPSwap analytics timed out.')), 4000); })]);
    const at = metricTimes.get(rows) ?? Date.now(); metricTimes.set(rows, at);
    metricCache = { rows, at }; return { rows, at };
  } catch {
    if (metricCache && Date.now() - metricCache.at < 300_000) return { rows: metricCache.rows, at: metricCache.at, error: 'ICPSwap analytics could not refresh; the last retrieved figures are shown.' };
    return { rows: null, at: null, error: 'ICPSwap price and volume analytics are unavailable. The on-chain token catalog is still available.' };
  } finally { clearTimeout(timer); }
}
let catalogRequest: Promise<DexRegistry> | undefined;
export function loadDexRegistry(): Promise<DexRegistry> {
  if (catalogRequest) return catalogRequest;
  catalogRequest = (async () => {
    const overlay = metrics();
    const catalog = await readDexCatalog();
    const info = await overlay;
    return applyDexMetrics(catalog, info.rows, info.at, info.error);
  })().finally(() => { catalogRequest = undefined; });
  return catalogRequest;
}
